<template>
    <div class="sf__container">
        <template v-if="ready && results.length > 0">
            <div class="sf__results-layout">
            <Filters
                v-if="popup || showFilters"
                :global-selections="selections"
                :foam="isFoam()"
                :vehicle="vehicle"
                :popup="popup"
                :results="results"
                @onApply="applyFilters"
                :show-filters="showFilters"
                @closeFilters="showFilters = false"
                />
            <Results
                @updateFilters="showFilters = true"
                :vehicle="vehicle"
                :vehicle-id="vehicle.id"
                v-if="!popup"
                :results="filtered_results"
                :foam="isFoam()"
                :foam-only="foamOnly"
                :vehicle-params="vehicleParams"
            />
            </div> 
        </template>
        <!-- <Results  /> -->
    </div>
</template>


<script>
import Filters from './Filters.vue'
import Results from './Results.vue'
import Searcher from '../helpers/searcher'; 
import Part from '../models/Part'; 
import Result from '../models/Result'; 
import Vehicle from '../models/Vehicle'; 
import store from '../store'; 
import Storage from '../helpers/storage'; 
import {slugify} from '../helpers/helpers'; 
import {FITMENT_KEYS, storeCurrentSelection} from '../helpers/currentSelection';
import FiltersBuilder from '../helpers/filters';
import {isFoamOnlyResponse} from '../helpers/foamOnlyNotice';

export default {

    props: ['slug', 'foam'], 

    components: {
        Filters, 
        Results
    }, 

    data() {
        return {
            products_loaded: false, 
            popup: true,
            showFilters: false, 
            results: [], 
            selections: {}, 
            ignored: [], 
            vehicle: null,
            vehicleParams: {},
            foamOnly: false,
            ready: false
        }
    }, 

    async mounted() {
        this.initialSearchString = window.location.search;

        const urlParams = new URLSearchParams(this.initialSearchString);
        const referrer = document.referrer;

        if (referrer && referrer.split('?')[0] !== window.location.href.split('?')[0]) {
            sessionStorage.setItem('sf_origin', referrer);
        }
        let params = {
            year: urlParams.get('year'), 
            make: urlParams.get('make'), 
            model: urlParams.get('model'), 
            submodel: urlParams.get('submodel')
        }

        params = await this.resolveVehicleParams(params);
        this.vehicleParams = params;

        let hits = [];
        const filters = Searcher.buildFilters(params);

        try {
            // Here we could paginate and load everything. 
            const resultsRequest = Searcher.search( this.isFoam() ? 'foam durofoam' : '*', {
                optionalWords: this.isFoam() ? 'foam durofoam' : null, 
                filters, 
                hitsPerPage: 2000
            });
            // A foam search alone cannot tell us whether this vehicle has covers.
            // Check all vehicle products before claiming covers are unavailable.
            const availabilityRequest = this.isFoam()
                ? Searcher.search('*', {
                    filters,
                    hitsPerPage: 2000,
                    attributesToRetrieve: ['options.part']
                }).catch(() => null)
                : null;
            const [response, availability] = await Promise.all([resultsRequest, availabilityRequest]);
            hits = response.hits || [];
            this.foamOnly = this.hasCompleteVehicleParams(params)
                && isFoamOnlyResponse(this.isFoam() ? availability : response);
        } catch (error) {
            console.error('SeatShop part results search failed:', error);
            return;
        }

        if (hits.length < 1) {
            return;
        }

        try {
            const mappedResults = hits.map( hit => new Result(hit, new Vehicle(hit.vehicle)));
            this.results.splice(0, this.results.length, ...mappedResults);

            this.vehicle = this.results[0].vehicle; 

            store.results = this.results; 
            store.vehicle = this.vehicle; 
            store.collection = this.getSlug() || slugify(Object.values(params).join('-'));
            store.filters = await (new FiltersBuilder(this.results)).build({
                vehicle_id: this.vehicle.id
            });

            const urlSelections = this.loadUrlSelections();
            this.selections = this.normalizeSelections(urlSelections);
            const hasCompleteUrlSelection = this.hasCompleteSelection(this.selections);
            const drawerAvailable = document.querySelector('.seatshop-vehicle-drawer') != null;

            if (hasCompleteUrlSelection) {
                this.selections = this.filledSelections(this.selections);
            }

            this.popup = !hasCompleteUrlSelection && !drawerAvailable;
            this.ready = true;
            this.$forceUpdate();

            storeCurrentSelection({
                vehicleParams: params,
                selections: this.selections,
                foam: this.isFoam(),
                handle: store.collection
            });

            if (!hasCompleteUrlSelection && drawerAvailable) {
                this.openSelectionDrawer(params);
            }
        } catch (error) {
            console.error('SeatShop part results setup failed:', error);
        }
    }, 

    computed: {
        filtered_results() {

            // If not items have been selected, we just return empty. 
            let not_all_selected = Object.keys(this.selections).length > 0 
                && Object.keys( this.selections ).some( key => {
                    return [null, ''].includes(this.selections[key])
                        && !this.ignored.includes(key); 
                }); 

            if ( not_all_selected ) {
                return []; 
            }

            return this.results.filter( result => {
                for (const key in this.selections) {
                    // Here it's ok to skip empty options because we need to show them anyways. 
                    if ( !result.optionMatches(key, this.selections[key]) && !result.optionIsEmpty(key) ) {
                        return false; 
                    }
                }

                return true; 
            }).map( result => {
                let part = new Part(result); 
                    // part.setVehicle( this.vehicle ); 
                return part; 
            } );
        },

        block_body() {
            return this.popup || this.showFilters; 
        }
    }, 

    watch: {
        block_body(block) {
            const body = document.querySelector('body');
            if ( block ) {
                body.style.position = 'fixed';
                body.style.height = '100%'; 
            } else {
                body.style.position = '';
                body.style.height = ''; 
            }
        }
    }, 

    methods: {
        hasCompleteVehicleParams(params) {
            return ['year', 'make', 'model', 'submodel'].every(key => {
                return Searcher.isValidFilterValue(params[key]);
            });
        },

        async resolveVehicleParams(params) {
            if (this.hasCompleteVehicleParams(params)) {
                return params;
            }

            const handle = this.getSlug();
            if (!handle) return params;

            try {
                const year = handle.match(/^\d{4}/)?.[0];
                if (year) {
                    const resolved = {year};
                    let remainder = handle.replace(/^\d{4}-?/, '');

                    for (const facet of ['make', 'model', 'submodel']) {
                        const facetName = `vehicle.${facet}`;
                        const response = await Searcher.search('*', {
                            filters: Searcher.buildFilters(resolved),
                            facets: [facetName],
                            hitsPerPage: 0,
                            maxValuesPerFacet: 1000
                        });
                        const value = this.matchHandleFacet(response.facets?.[facetName], remainder);

                        if (!value) break;

                        resolved[facet] = value;
                        remainder = remainder
                            .replace(new RegExp(`^${slugify(value)}-?`), '')
                            .replace(/^-+/, '');
                    }

                    if (this.hasCompleteVehicleParams(resolved)) {
                        return resolved;
                    }
                }

                const response = await Searcher.search(handle.replace(/-/g, ' '), {
                    filters: 'active:true',
                    hitsPerPage: 2000
                });
                const hits = response.hits || [];
                const matchingHit = hits.find(hit => {
                    return slugify(hit.vehicle?.collection_name || '') === handle;
                }) || hits[0];
                const vehicle = matchingHit?.vehicle;

                if (!vehicle) return params;

                return {
                    year: handle.match(/^\d{4}/)?.[0] || vehicle.start_year,
                    make: vehicle.make,
                    model: vehicle.model,
                    submodel: vehicle.submodel
                };
            } catch (error) {
                return params;
            }
        },

        matchHandleFacet(facetValues, remainder) {
            return Object.keys(facetValues || {})
                .sort((a, b) => slugify(b).length - slugify(a).length)
                .find(value => {
                    const valueSlug = slugify(value);
                    return remainder === valueSlug || remainder.startsWith(`${valueSlug}-`);
                });
        },

        normalizeSelections(selections) {
            const normalized = {...selections};

            store.filters
                .filter(filter => ['vehicle', 'part'].includes(filter.type))
                .forEach(filter => {
                    const selectedValue = normalized[filter.slug];

                    // Missing options are valid on the PLP. The drawer can omit an
                    // unavailable material option (for example Pattern), and older
                    // links may explicitly send `null` for that omitted option.
                    if ([undefined, null, '', 'null'].includes(selectedValue)) {
                        delete normalized[filter.slug];
                        return;
                    }

                    // Retain every valid URL selection, including a single-value
                    // option, because it still constrains the products displayed.
                    if (!filter.values.some(value => value.value == selectedValue)) {
                        normalized[filter.slug] = null;
                    }
                });

            return normalized;
        },

        hasConfigurableOptions() {
            return store.filters
                .filter(filter => ['vehicle', 'part'].includes(filter.type))
                .some(filter => filter.values.length > 1);
        },

        hasCompleteSelection(selections) {
            const selectedKeys = Object.keys(selections).filter(key => {
                return selections[key] != null && selections[key] !== '';
            });

            // A collection URL without selections only needs the drawer when the
            // vehicle actually has a fitment or material choice. Vehicles with no
            // configurable options should render their products immediately.
            if (!selectedKeys.length) return !this.hasConfigurableOptions();

            // Once the URL contains selections, the PLP must render whenever those
            // selections match products; it must not reopen the drawer merely
            // because another product attribute was omitted.
            if (Object.keys(selections).some(key => selections[key] == null)) return false;

            return this.results.some(result => {
                return selectedKeys.every(key => {
                    return result.optionMatches(key, selections[key])
                        || result.optionIsEmpty(key);
                });
            });
        },

        filledSelections(selections) {
            return Object.keys(selections).reduce((filled, key) => {
                if (selections[key] != null && selections[key] !== '') {
                    filled[key] = selections[key];
                }

                return filled;
            }, {});
        },

        openSelectionDrawer(vehicleParams) {
            const missingFitment = FITMENT_KEYS.some(key => this.selections[key] == null);
            const step = missingFitment ? 'fitment' : 'material';
            const selection = {
                vehicleParams: {...vehicleParams},
                selections: {...this.selections},
                foam: this.isFoam(),
                handle: store.collection
            };

            this.$nextTick(() => {
                document.dispatchEvent(new CustomEvent('seatshop:vehicle-drawer-open-request', {
                    detail: {
                        step,
                        selection,
                        resetAfterStep: step
                    }
                }));
            });
        },

        applyFilters(data) {
            // Adds relevant information to history state
            const queryString = new URLSearchParams(document.location.search);
            Object.keys(data.selections).forEach(
                key => queryString.set(key, data.selections[key]),
            );
            history.pushState(null, null, '?' + queryString.toString());

            this.selections = data.selections;
            this.ignored = data.ignored;  
            this.popup = false; 

            this.storeSelectionsInStorage(); 
            storeCurrentSelection({
                vehicleParams: {
                    year: new URLSearchParams(window.location.search).get('year'),
                    make: new URLSearchParams(window.location.search).get('make'),
                    model: new URLSearchParams(window.location.search).get('model'),
                    submodel: new URLSearchParams(window.location.search).get('submodel')
                },
                selections: this.selections,
                foam: this.isFoam(),
                handle: store.collection
            });
            document.dispatchEvent(new CustomEvent('seatshop:current-selection-updated', {
                detail: {
                    selection: {
                        vehicleParams: {
                            year: new URLSearchParams(window.location.search).get('year'),
                            make: new URLSearchParams(window.location.search).get('make'),
                            model: new URLSearchParams(window.location.search).get('model'),
                            submodel: new URLSearchParams(window.location.search).get('submodel')
                        },
                        selections: this.selections,
                        foam: this.isFoam(),
                        handle: store.collection
                    }
                }
            }));
        }, 

        getSlug() {
            if ( this.slug ) {
                return this.slug; 
            }

            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('slug') || null; 
        }, 

        isFoam() {
            if ( this.foam ) {
                return true; 
            }

            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('foam') == 'true' ? true : false; 
        }, 

        /**
         * We store selections in sessionStorage to have them available in the future. 
         */
        storeSelectionsInStorage() {
            Storage.storeSelections(store.collection, this.selections); 
        }, 

        /**
         * Allows us to pull selections from storage to show them back as they were. 
         */
        loadUrlSelections() {
            let keys = [...new Set(this.results.reduce( (keys, result) => {
                return keys.concat(Object.keys(result.options || {})); 
            }, []))]; // this gives us all options from the result set in algolia. 
            let selections = {}; 

            // Here we only match those in the keys. 
            ( new URLSearchParams(window.location.search)).forEach( (value, key) => {
                if ( keys.includes(key)) {
                    selections[key] = value; 
                }
            });

            return selections;
        }
    }
}
</script>
